import React, { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/axios";

type Row = {
  id: number;
  type: string;
  labelFr?: string;
  entityType: string;
  entityId: number;
  clientId: number | null;
  passageId: number | null;
  tourneeId: number | null;
  userId: number | null;
  payload: unknown;
  createdAt: string;
};

type AuditPageResponse = {
  success?: boolean;
  items?: Row[];
  nextCursor?: number | null;
  hasMore?: boolean;
  warning?: string;
};

export default function AdminAuditOperationalPage(): React.JSX.Element {
  const [clientId, setClientId] = useState("");
  const [passageId, setPassageId] = useState("");
  const [tourneeId, setTourneeId] = useState("");
  const [type, setType] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [take, setTake] = useState("100");
  const [items, setItems] = useState<Row[]>([]);
  const [nextCursor, setNextCursor] = useState<number | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPage = useCallback(
    async (opts: { append: boolean; cursorId?: number }) => {
      const params = new URLSearchParams();
      if (clientId.trim()) params.set("clientId", clientId.trim());
      if (passageId.trim()) params.set("passageId", passageId.trim());
      if (tourneeId.trim()) params.set("tourneeId", tourneeId.trim());
      if (type.trim()) params.set("type", type.trim());
      if (from.trim()) params.set("from", from.trim());
      if (to.trim()) params.set("to", to.trim());
      const nTake = Number(take);
      params.set("take", Number.isFinite(nTake) && nTake > 0 ? String(Math.min(nTake, 500)) : "100");
      if (opts.append && opts.cursorId != null) {
        params.set("cursorId", String(opts.cursorId));
      }
      const { data } = await api.get<AuditPageResponse>(`/admin/audit-operational?${params.toString()}`);
      const chunk = Array.isArray(data.items) ? data.items : [];
      if (opts.append) {
        setItems((prev) => [...prev, ...chunk]);
      } else {
        setItems(chunk);
      }
      setNextCursor(data.nextCursor ?? null);
      setHasMore(Boolean(data.hasMore));
    },
    [clientId, passageId, tourneeId, type, from, to, take]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await fetchPage({ append: false });
    } catch (e) {
      console.error(e);
      setError("Impossible de charger l’audit opérationnel.");
      setItems([]);
      setNextCursor(null);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [fetchPage]);

  const loadMore = useCallback(async () => {
    if (nextCursor == null || !hasMore || loadingMore) return;
    setLoadingMore(true);
    setError(null);
    try {
      await fetchPage({ append: true, cursorId: nextCursor });
    } catch (e) {
      console.error(e);
      setError("Impossible de charger la suite.");
    } finally {
      setLoadingMore(false);
    }
  }, [fetchPage, nextCursor, hasMore, loadingMore]);

  useEffect(() => {
    void load();
    // Chargement initial uniquement ; les filtres se réappliquent via le bouton.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-6 text-slate-100">
      <div>
        <h1 className="text-xl font-bold text-white">Audit opérationnel</h1>
        <p className="mt-1 text-sm text-slate-400">
          Événements persistés (<code className="text-slate-300">operational_events</code>) — filtres combinables,
          pagination par curseur (<code className="text-slate-300">cursorId</code>).
        </p>
      </div>

      <div className="grid gap-3 rounded-xl border border-slate-800 bg-slate-900/60 p-4 md:grid-cols-3 lg:grid-cols-7">
        <label className="text-xs font-semibold text-slate-400">
          Client ID
          <input
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm"
            inputMode="numeric"
          />
        </label>
        <label className="text-xs font-semibold text-slate-400">
          Passage ID
          <input
            value={passageId}
            onChange={(e) => setPassageId(e.target.value)}
            className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm"
            inputMode="numeric"
          />
        </label>
        <label className="text-xs font-semibold text-slate-400">
          Tournée ID
          <input
            value={tourneeId}
            onChange={(e) => setTourneeId(e.target.value)}
            className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm"
            inputMode="numeric"
          />
        </label>
        <label className="text-xs font-semibold text-slate-400">
          Type (code)
          <input
            value={type}
            onChange={(e) => setType(e.target.value)}
            placeholder="PASSAGE_COMPLETED"
            className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm"
          />
        </label>
        <label className="text-xs font-semibold text-slate-400">
          Du (YYYY-MM-DD)
          <input
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm"
          />
        </label>
        <label className="text-xs font-semibold text-slate-400">
          Au (YYYY-MM-DD)
          <input
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm"
          />
        </label>
        <label className="text-xs font-semibold text-slate-400">
          Limite
          <input
            value={take}
            onChange={(e) => setTake(e.target.value)}
            className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm"
            inputMode="numeric"
          />
        </label>
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => void load()}
          disabled={loading}
          className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-500 disabled:opacity-50"
        >
          {loading ? "Chargement…" : "Appliquer filtres"}
        </button>
        {hasMore && nextCursor != null ? (
          <button
            type="button"
            onClick={() => void loadMore()}
            disabled={loadingMore}
            className="rounded-lg border border-slate-600 bg-slate-800 px-4 py-2 text-sm font-semibold text-slate-100 hover:bg-slate-700 disabled:opacity-50"
          >
            {loadingMore ? "Chargement…" : "Charger la suite"}
          </button>
        ) : null}
      </div>

      {error ? <p className="text-sm text-rose-300">{error}</p> : null}

      <div className="overflow-x-auto rounded-xl border border-slate-800">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-slate-900 text-xs uppercase text-slate-400">
            <tr>
              <th className="px-3 py-2">Date</th>
              <th className="px-3 py-2">Événement</th>
              <th className="px-3 py-2">Code</th>
              <th className="px-3 py-2">Entité</th>
              <th className="px-3 py-2">Client</th>
              <th className="px-3 py-2">Passage</th>
              <th className="px-3 py-2">Tournée</th>
            </tr>
          </thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.id} className="border-t border-slate-800/80 bg-slate-950/40">
                <td className="whitespace-nowrap px-3 py-2 text-slate-300">
                  {new Date(r.createdAt).toLocaleString("fr-FR")}
                </td>
                <td className="px-3 py-2 text-slate-100">{r.labelFr ?? r.type.replace(/_/g, " ")}</td>
                <td className="px-3 py-2 font-mono text-[10px] text-slate-500">{r.type}</td>
                <td className="px-3 py-2 text-slate-300">
                  {r.entityType} #{r.entityId}
                </td>
                <td className="px-3 py-2">{r.clientId ?? "—"}</td>
                <td className="px-3 py-2">{r.passageId ?? "—"}</td>
                <td className="px-3 py-2">{r.tourneeId ?? "—"}</td>
              </tr>
            ))}
            {items.length === 0 && !loading ? (
              <tr>
                <td colSpan={7} className="px-3 py-8 text-center text-slate-500">
                  Aucun événement pour ces critères.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}
